/**
 * @fileoverview Point-of-Interest (POI) Icon Set - Icons for facility directory & venue maps
 * @module icons/poi-icons
 * @version 1.0.0
 *
 * @description
 * A curated set of point-of-interest icons for use in interactive facility directory maps
 * and events venue maps (indoor and outdoor). Covers essential wayfinding categories:
 * amenities, services, venue elements, and outdoor facilities.
 *
 * Usage:
 *   import '@cliftontechlabs/ui-kit/icons';
 *   <ctl-icon name="restroom"></ctl-icon>
 *   <ctl-icon name="parking"></ctl-icon>
 *   <ctl-icon name="first-aid"></ctl-icon>
 *
 * All icons are 24x24 viewBox and use currentColor for theming.
 */

export const POI_ICONS = {
  // ── Wayfinding & Access ─────────────────────────────────────────────────

  /**
   * restroom – Male (rectangle body) and female (triangle/dress body) figures side-by-side.
   * Universal WC / toilet facility marker.
   */
  "restroom": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM5 8h4v11H5zm10 0h4l-2 11z"/>
    </svg>
  `,

  /**
   * elevator – Hollow square frame with an upward-pointing triangle in the top half
   * and a downward-pointing triangle in the bottom half.
   * Indicates elevator / lift access.
   */
  "elevator": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zM12 7l4 4H8zm0 10-4-4h8z"/>
    </svg>
  `,

  /**
   * parking – Hollow square frame containing a bold letter P with a hollow bowl.
   * Standard parking facility marker.
   */
  "parking": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zM8 7h6v5h-4v5H8V7zm2 2v2h3V9h-3z"/>
    </svg>
  `,

  /**
   * escalator – Diagonal ramp/band from upper-right to lower-left with a human figure
   * at the top and descending steps below.
   * Indicates escalator access.
   */
  "escalator": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 2h5v2L9 20H3v-2l12-16zm-5.5-.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM9 8h4l-1 2h-1v3H9v-3H8z"/>
    </svg>
  `,

  // ── Food & Beverage ─────────────────────────────────────────────────────

  /**
   * cafe – Coffee mug with two steam wisps above and a D-shaped handle on the right.
   * Indicates a café, coffee shop, or refreshment stand.
   */
  "cafe": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5c0-1.5-1-2-1-3h2c0 1 1 1.5 1 3H8zm4 0c0-1.5-1-2-1-3h2c0 1 1 1.5 1 3h-2zM4 6h14v2H4zM5 8h12v12H5zm12 2h1.5a2 2 0 0 1 0 4H17v-4z"/>
    </svg>
  `,

  /**
   * restaurant – Two-tine fork (left) beside a chef's knife (right).
   * Indicates a restaurant, dining hall, or food court.
   */
  "restaurant": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2h1v5H7zm2 0h1v5H9zM7 7h3v2H7zM8 9h1v13H8zM14 2h3v8h-3zm.5 8h2v12h-2z"/>
    </svg>
  `,

  /**
   * bar – Cocktail glass (V-shaped bowl on a stem with a base).
   * Indicates a bar, lounge, or drinks station.
   */
  "bar": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3h14L12 13v6h3v2H9v-2h3v-6z"/>
    </svg>
  `,

  // ── Safety & Health ─────────────────────────────────────────────────────

  /**
   * first-aid – Hollow square frame with a solid medical cross (plus sign) centred inside.
   * Indicates a first-aid station, medical room, or AED location.
   */
  "first-aid": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm5 3h2v3h3v2h-3v3h-2v-3H7v-2h3V8z"/>
    </svg>
  `,

  /**
   * exit-sign – Hollow square frame with two horizontal bars (text simulation) on the
   * left and a left-pointing arrow on the right. Represents a directional exit/way-out sign.
   */
  "exit-sign": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zM7 8h6v2H7zm0 4h6v2H7zM17 9l-4 3 4 3z"/>
    </svg>
  `,

  // ── Venue & Events ──────────────────────────────────────────────────────

  /**
   * ticket – A two-part ticket shape: a narrower stub on the left separated from the main
   * body on the right by circular notches, with two small perforation squares on the main body.
   * Indicates a ticket booth, box office, or entry point.
   */
  "ticket": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 7h8v2a2 2 0 0 0 0 6v2H2V7zm8 0h12v10H10v-2a2 2 0 0 0 0-6V7zm2 3v2h2v-2h-2zm0 4v2h2v-2h-2z"/>
    </svg>
  `,

  /**
   * stage – A wide top bar (proscenium), two tall vertical curtains on the left and right,
   * and a thin floor bar at the bottom, leaving the stage centre open.
   * Indicates a stage, performance area, or main arena.
   */
  "stage": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 3h20v3H2zM3 6h4v14H3zM17 6h4v14h-4zM3 20h18v2H3z"/>
    </svg>
  `,

  /**
   * vip-lounge – Star shape with a rounded rectangular base/plinth beneath it.
   * Indicates a VIP area, premium lounge, or exclusive access zone.
   */
  "vip-lounge": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l2.4 4.8 5.3.8-3.8 3.7.9 5.2L12 14l-4.8 2.5.9-5.2L4.3 7.6l5.3-.8zm-6 15h12v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-2z"/>
    </svg>
  `,

  // ── Retail & Services ───────────────────────────────────────────────────

  /**
   * shop – A wide sign/awning bar at the top and a solid building block below with a
   * centred hollow door opening.
   * Indicates a shop, retail unit, merchandise stand, or gift store.
   */
  "shop": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 2h20v6H2zM4 8h16v14H4zm5 4v6h6v-6H9z"/>
    </svg>
  `,

  /**
   * coat-check – Clothes hanger: a circle hook at the top, a curved neck, two long arms
   * spreading left and right, and a triangular clothes-support frame below the cross-bar.
   * Indicates a coat check, cloakroom, or bag-drop facility.
   */
  "coat-check": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 5a3 3 0 0 1 2.8 2L21 11v2h-7v9h-4v-9H3v-2l6.2-2A3 3 0 0 1 12 7z"/>
    </svg>
  `,

  // ── Communication & Connectivity ────────────────────────────────────────

  /**
   * wifi – Three concentric quarter-circle arcs with a filled dot at the centre-bottom.
   * Indicates a WiFi hotspot or connectivity zone.
   */
  "wifi": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 18.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm-5-4.5a7 7 0 0 1 10 0l1.5-1.5A9 9 0 0 0 12 10a9 9 0 0 0-6.5 2.5zm-4-4a13 13 0 0 1 18 0l1.5-1.5a15 15 0 0 0-21 0z"/>
    </svg>
  `,

  /**
   * phone – Telephone handset silhouette (curved receiver with earpiece and mouthpiece).
   * Indicates a public phone, information desk, or call point.
   */
  "phone": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
    </svg>
  `,

  // ── Outdoor & Temporary Structures ─────────────────────────────────────

  /**
   * tent – Large filled triangle (outer shell) with a smaller hollow inner triangle creating
   * a tent-outline effect, plus a small filled rectangle at the base centre for the door.
   * Indicates an outdoor tent, marquee, pavilion, or temporary venue structure.
   */
  "tent": `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2 23 21H1zM12 7l-9 13h18zM11 16h2v4h-2z"/>
    </svg>
  `,
};
